import React, { useState, useEffect, useCallback, useRef } from "react";

import { useHistory, useLocation, useParams } from "react-router";

import { Form, Formik, Field } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";

import {
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Select,
  Button,
  Input,
  HStack,
  Text,
  Tooltip,
  Icon,
  Box,
  Grid,
  GridItem,
} from "@chakra-ui/react";

import LoadingSpinner from "../../../../components/LoadingSpinner";

import PerfilSAService from "../../../../../main/services/PerfilSAService";
import turnToPascalCase from "../../../../utils/turnToPascal";

import { forNames, onlyNumbers as numbersMask } from "../../../../utils/masks";
import { CustomMultiSelectWField } from "../../../../components/MultiSelect";
import CargaSelecaoService from "../../../../../main/services/CargaSelecaoService";
import { IoHelpCircle } from "react-icons/io5";
import { CustomInputWField } from "../../../../components/CustomInputWField";
import Group from "../../Visualizar/Group";
import { hasFormChanged } from "../../../../utils/hasFormChanged";
import { criancaFunc, servicos } from "../../../../../helpers/Funcionalidades";
import SemRegistros from "../../../../components/SemRegistros";
import { useAppContext } from "../../../../../Context/MessageContextProvider";
import { useAuthProvider } from "../../../../../Context/AuthProvider";
import { scrollToFieldAnexo } from "../../../../utils/scrollToFIeldAnexo";

const CadastrarPerfil = () => {
  const { id } = useParams();
  const history = useHistory();
  const [generos, setGeneros] = useState({ options: [], isLoading: false });
  const [loading, setLoading] = useState(true);
  const { roleKeycloack, user } = useAuthProvider();
  const { setCadastrarPerfilServ, idPJ } = useAppContext();
  const [initialValues] = useState({
    restricaoGenero: "",
    restricoes: "",
    restricaoGeneroOthers: "",
    restricaoIdade: "",
    idadeMinima: 0,
    idadeMaxima: 0,
    restricaoDeficiencia: "",
    restricaoSubstancias: "",
    restricaoCuidados: "",
    capacidadeAcolhimento: "",
  });

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const idTipoPendencia = searchParams.get("idTipoPendencia");

  const anexoRef = useRef(null);

  const scrollToField = useCallback(
    (ref) => {
      scrollToFieldAnexo(idTipoPendencia, ref);
    },
    [idTipoPendencia],
  );

  useEffect(() => {
    if (["57"].includes(String(idTipoPendencia))) {
      const timeout = setTimeout(() => {
        requestAnimationFrame(() => {
          if (anexoRef.current) {
            scrollToField(anexoRef);
          }
        });
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [idTipoPendencia, scrollToField]);

  useEffect(() => {
    PerfilSAService.getPerfilSA(id)
      .then((res) => {
        Swal.fire({
          title: "Atenção!",
          text: "Já existe um Perfil cadastrado para este Serviço de Acolhimento!",
          icon: "warning",
          confirmButtonText: "Fechar",
          confirmButtonColor: "#169499",
        }).then(() => {
          history.push(`/servico-acolhimento/visualizar-perfil/${id}`);
        });
      })
      .catch((erro) => {
        if (erro.response) {
          if (erro.response.data.status !== 404) {
            Swal.fire({
              title: "Atenção!",
              text: "Já existe um Perfil cadastrado para este Serviço de Acolhimento!",
              icon: "warning",
              confirmButtonText: "Fechar",
              confirmButtonColor: "#169499",
            }).then(() => {
              history.push(`/servico-acolhimento/visualizar-perfil/${id}`);
            });
          } else {
            setLoading(false);
          }
        }
      });
  }, [history, id]);

  const schema = Yup.object().shape({
    restricaoGenero: Yup.string().required("Este campo é obrigatório!"),
    restricoes: Yup.array().when("restricaoGenero", {
      is: (rGenero) => rGenero === "S",
      then: Yup.array()
        .required("Selecione ao menos 1!")
        .test("allSelected", function (value) {
          if (!value || !value[0]?.length) {
            return this.createError({
              path: "restricoes",
              message: "Selecione ao menos 1!",
            });
          }

          if (value[0].length === 8) {
            return this.createError({
              path: "restricoes",
              message:
                "O Serviço de Acolhimento não pode fazer restrição de todos os gêneros!",
            });
          }

          if (
            value[0].length === 7 &&
            !value.some((array) => array.some((item) => item.id === 8))
          ) {
            return this.createError({
              path: "restricoes",
              message:
                "O Serviço de Acolhimento não pode fazer restrição de todos os gêneros!",
            });
          }

          return true;
        }),
    }),
    restricaoGeneroOthers: Yup.string().when("restricoes", {
      is: (restricoes) =>
        restricoes?.some(
          (array) =>
            Array.isArray(array) && array.some((item) => item.id === 8),
        ),
      then: Yup.string()
        .required("Este campo é obrigatório!")
        .trim("Este campo é obrigatório!"),
    }),
    restricaoIdade: Yup.string().required("Este campo é obrigatório!"),
    idadeMinima: Yup.number().when("restricaoIdade", {
      is: (rIdade) => rIdade === "S",
      then: Yup.number()
        .required("Este campo é obrigatório!")
        .min(0, "A idade mínima para este campo é 0!")
        .max(16, "A idade máxima para este campo é 16!"),
    }),
    idadeMaxima: Yup.number().when("restricaoIdade", {
      is: (rIdade) => rIdade === "S",
      then: Yup.number()
        .required("Este campo é obrigatório!")
        .max(17, "A idade máxima para este campo é 17!")
        .when("idadeMinima", (idadeMinima, schema) => {
          return idadeMinima === 0
            ? schema.min(1, "A idade mínima para este campo é 1!")
            : schema.min(
                idadeMinima + 1,
                "A idade máxima não pode ser menor ou igual a idade mínima",
              );
        }),
    }),
    restricaoDeficiencia: Yup.string().required("Este campo é obrigatório!"),
    restricaoSubstancias: Yup.string().required("Este campo é obrigatório!"),
    restricaoCuidados: Yup.string().required("Este campo é obrigatório!"),
    capacidadeAcolhimento: Yup.number()
      .required("Este campo é obrigatório!")
      .positive("A capacidade deve ser maior que zero!"),
  });

  const handleGetGeneros = () => {
    setGeneros({ ...generos, isLoading: true });

    CargaSelecaoService.getGeneros().then((res) => {
      res.data.push({
        id: 8,
        genero: "OUTROS",
        descricao: "Descrição do gênero Outros",
      });
      const tempOptions = res.data.map((genero) => {
        return {
          id: genero?.id,
          descricao: genero?.genero,
          label: (
            <HStack justifyContent="space-between" px="3px">
              <Text
                color="#1A203D"
                fontWeight={400}
                fontSize="14px"
                w={180}
                lineHeight="19.6px"
              >
                {genero?.genero}
              </Text>
              <Tooltip
                label={genero?.descricao}
                fontSize="md"
                textAlign={"justify"}
              >
                <span className="hint-tooltip">
                  <Icon
                    aria-label="Aviso de pendente"
                    color="#6B6C71"
                    as={IoHelpCircle}
                    boxSize="20px"
                  />
                </span>
              </Tooltip>
            </HStack>
          ),
        };
      });
      setGeneros({ options: tempOptions, isLoading: false });
    });
  };

  const handleSubmit = (values, actions) => {
    setTimeout(() => {
      const { restricoes, restricaoGeneroOthers, ...resto } = values;

      const tempRestricoes =
        restricoes[0]?.length !== 0 && values.restricaoGenero === "S"
          ? restricoes[0].map((item) => {
              if (item.descricao === "OUTROS") {
                return turnToPascalCase(restricaoGeneroOthers);
              } else {
                return item.descricao;
              }
            })
          : null;

      const jsonString = tempRestricoes
        ? JSON.stringify(tempRestricoes.map((item) => turnToPascalCase(item)))
        : null;
      const valuesToSend = {
        restricoes: jsonString,
        ...resto,
      };

      PerfilSAService.novaPerfilSA(id, valuesToSend)
        .then((res) => {
          Swal.fire({
            title: "Sucesso!",
            text: "Registro cadastrado com sucesso!",
            icon: "success",
            confirmButtonText: "Continuar",
            confirmButtonColor: "#169499",
          }).then(function () {
            history.push("/servico-acolhimento/visualizar-perfil/" + id);
            setCadastrarPerfilServ(false);
          });
          actions.setSubmitting(false);
        })
        .catch((error) => {
          if (error.response.data.field === "perfil") {
            Swal.fire({
              title: "Atenção!",
              text: error.response.data.message,
              icon: "warning",
              confirmButtonText: "Fechar",
              confirmButtonColor: "#169499",
            }).then(() => {});
          }
          if (error.response) {
            if (error.response.data) {
              if (error.response.data.apierror) {
                if (error.response.data.apierror.subErrors) {
                  error.response.data.apierror.subErrors.forEach((erro) => {
                    actions.setFieldError(erro.field, erro.message);
                  });
                }
              } else {
                actions.setFieldError(
                  error.response && error.response.data.field,
                  error.response && error.response.data.message,
                );
              }
            }
          }
          actions.setSubmitting(false);
        });
    }, 200);
  };

  return !loading ? (
    roleKeycloack &&
    (roleKeycloack.includes(servicos.perfilCadastrar) ||
      (roleKeycloack?.includes(criancaFunc.visualizarTudoAcolhimento) &&
        user?.orgaoExterno?.includes(idPJ))) ? (
      <Formik
        validationSchema={schema}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {(props) => (
          <Form>
            {setCadastrarPerfilServ(
              hasFormChanged(initialValues, props.values),
            )}
            <Box
              mt={"15px"}
              border={
                ["57"].includes(idTipoPendencia) ? "3px solid #4299E199" : ""
              }
              ref={anexoRef}
            >
              <Group title="Gênero" mt={"0px"}>
                <Field name="restricaoGenero">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={
                        form.errors.restricaoGenero &&
                        form.touched.restricaoGenero
                      }
                      id="restricaoGenero"
                      mt="15px"
                    >
                      <FormLabel
                        color="#5F6377"
                        fontWeight="500"
                        fontSize="14px"
                        lineHeight="19.6px"
                        htmlFor="restricaoGenero"
                      >
                        Faz Restrição de Gênero?*
                      </FormLabel>
                      <Select
                        {...field}
                        h="42px"
                        name="restricaoGenero"
                        id="restricaoGenero"
                        placeholder="Selecione"
                        backgroundColor="white"
                        width={["100%", "100%", "30%"]}
                      >
                        <option value="S">Sim</option>
                        <option value="N">Não</option>
                      </Select>
                      <FormErrorMessage>
                        {form.errors.restricaoGenero}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                {props.values.restricaoGenero === "S" && (
                  <Grid templateColumns="repeat(9, 1fr) 1fr">
                    <GridItem colSpan={{ base: 10, md: 10, lg: 3, xl: 3 }}>
                      <CustomMultiSelectWField
                        mt={2}
                        placeholderColor={"#1A202C"}
                        separatorFalse={true}
                        fieldName="restricoes"
                        label="Informe as Restrições*:"
                        placeholder="Selecione"
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.id}
                        isLoading={generos.isLoading}
                        options={generos.options}
                        values={props?.values?.restricoes[0] || ""}
                        onChange={(value) => {
                          props.setFieldValue("restricoes", value ? value : "");
                        }}
                        onFocus={
                          generos.options.length === 0 ? handleGetGeneros : null
                        }
                        visible={props?.values?.restricaoGenero === "S"}
                      />
                    </GridItem>
                  </Grid>
                )}

                {props?.values?.restricoes &&
                  props?.values?.restricoes?.some((array) =>
                    array?.some((item) => item.id === 8),
                  ) && (
                    <Grid templateColumns="repeat(9, 1fr) 1fr">
                      <GridItem colSpan={3}>
                        <CustomInputWField
                          mt={3}
                          label="Informe Outras Restrições *"
                          fieldName="restricaoGeneroOthers"
                          maxLength={40}
                          placeholder="Informe Outros"
                          onChange={(e) =>
                            props.setFieldValue(
                              "restricaoGeneroOthers",
                              forNames(e.target.value),
                            )
                          }
                        />
                      </GridItem>
                    </Grid>
                  )}
              </Group>

              <Group title="Faixa Etária">
                <Field name="restricaoIdade">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={
                        form.errors.restricaoIdade &&
                        form.touched.restricaoIdade
                      }
                      id="restricaoIdade"
                      mt="15px"
                    >
                      <FormLabel
                        color="#5F6377"
                        fontWeight="500"
                        fontSize="14px"
                        lineHeight="19.6px"
                        htmlFor="restricaoIdade"
                      >
                        Faz Restrição de Faixa Etária?*
                      </FormLabel>
                      <Select
                        {...field}
                        h="42px"
                        name="restricaoIdade"
                        id="restricaoIdade"
                        placeholder="Selecione"
                        backgroundColor="white"
                        width={["100%", "100%", "30%"]}
                      >
                        <option value="S">Sim</option>
                        <option value="N">Não</option>
                      </Select>
                      <FormErrorMessage>
                        {form.errors.restricaoIdade}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                {props.values.restricaoIdade === "S" && (
                  <>
                    <Field name="idadeMinima">
                      {({ field, form }) => (
                        <FormControl
                          isInvalid={
                            form.errors.idadeMinima && form.touched.idadeMinima
                          }
                          id="idadeMinima"
                        >
                          <FormLabel
                            color="#5F6377"
                            fontWeight="500"
                            fontSize="14px"
                            lineHeight="19.6px"
                            htmlFor="idadeMinima"
                            mt="15px"
                          >
                            Idade Mínima Aceita Pelo Serviço*:
                          </FormLabel>
                          <Input
                            {...field}
                            type="number"
                            h="42px"
                            name="idadeMinima"
                            id="idadeMinima"
                            backgroundColor="#F9FAFB"
                            autoComplete="off"
                            border="1px solid #D7D8DE"
                            placeholder="Informe mínima"
                            width={["100%", "100%", "30%"]}
                          />
                          <FormErrorMessage>
                            {form.errors.idadeMinima}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                    <Field name="idadeMaxima">
                      {({ field, form }) => (
                        <FormControl
                          isInvalid={
                            form.errors.idadeMaxima && form.touched.idadeMaxima
                          }
                          id="idadeMaxima"
                          mt="5px"
                        >
                          <FormLabel
                            color="#5F6377"
                            fontWeight="500"
                            fontSize="14px"
                            lineHeight="19.6px"
                            htmlFor="idadeMaxima"
                            mt="15px"
                          >
                            Idade Máxima Aceita Pelo Serviço*:
                          </FormLabel>
                          <Input
                            {...field}
                            type="number"
                            h="42px"
                            name="idadeMaxima"
                            id="idadeMaxima"
                            backgroundColor="#F9FAFB"
                            autoComplete="off"
                            border="1px solid #D7D8DE"
                            placeholder="Informe mínima"
                            width={["100%", "100%", "30%"]}
                          />
                          <FormErrorMessage>
                            {form.errors.idadeMaxima}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </>
                )}
              </Group>

              <Group title="Saúde">
                <Field name="restricaoDeficiencia">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={
                        form.errors.restricaoDeficiencia &&
                        form.touched.restricaoDeficiencia
                      }
                      id="restricaoDeficiencia"
                      mt="15px"
                    >
                      <FormLabel
                        color="#5F6377"
                        fontWeight="500"
                        fontSize="14px"
                        lineHeight="19.6px"
                        htmlFor="restricaoDeficiencia"
                      >
                        Faz Restrição de Criança/Adolescente com Deficiência?*
                      </FormLabel>
                      <Select
                        {...field}
                        h="42px"
                        name="restricaoDeficiencia"
                        id="restricaoDeficiencia"
                        placeholder="Selecione"
                        backgroundColor="white"
                        width={["100%", "100%", "30%"]}
                      >
                        <option value="S">Sim</option>
                        <option value="N">Não</option>
                      </Select>
                      <FormErrorMessage>
                        {form.errors.restricaoDeficiencia}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Field name="restricaoSubstancias">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={
                        form.errors.restricaoSubstancias &&
                        form.touched.restricaoSubstancias
                      }
                      id="restricaoSubstancias"
                      mt="15px"
                    >
                      <FormLabel
                        color="#5F6377"
                        fontWeight="500"
                        fontSize="14px"
                        lineHeight="19.6px"
                        htmlFor="restricaoSubstancias"
                      >
                        Faz Restrição de Criança/Adolescente que faz uso de
                        Substâncias Psicoativas?*
                      </FormLabel>
                      <Select
                        {...field}
                        h="42px"
                        name="restricaoSubstancias"
                        id="restricaoSubstancias"
                        placeholder="Selecione"
                        backgroundColor="white"
                        width={["100%", "100%", "30%"]}
                      >
                        <option value="S">Sim</option>
                        <option value="N">Não</option>
                      </Select>
                      <FormErrorMessage>
                        {form.errors.restricaoSubstancias}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Field name="restricaoCuidados">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={
                        form.errors.restricaoCuidados &&
                        form.touched.restricaoCuidados
                      }
                      id="restricaoCuidados"
                      mt="15px"
                    >
                      <FormLabel
                        color="#5F6377"
                        fontWeight="500"
                        fontSize="14px"
                        lineHeight="19.6px"
                        htmlFor="restricaoCuidados"
                      >
                        Faz Restrição de Criança/Adolescente que Demanda
                        Cuidados de Saúde?*
                      </FormLabel>
                      <Select
                        {...field}
                        h="42px"
                        name="restricaoCuidados"
                        id="restricaoCuidados"
                        placeholder="Selecione"
                        backgroundColor="white"
                        width={["100%", "100%", "30%"]}
                      >
                        <option value="S">Sim</option>
                        <option value="N">Não</option>
                      </Select>
                      <FormErrorMessage>
                        {form.errors.restricaoCuidados}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              </Group>
              <Group title="Capacidade">
                <Field name="capacidadeAcolhimento">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={
                        form.errors.capacidadeAcolhimento &&
                        form.touched.capacidadeAcolhimento
                      }
                      id="capacidadeAcolhimento"
                      mt="15px"
                    >
                      <FormLabel
                        color="#5F6377"
                        fontWeight="500"
                        fontSize="14px"
                        lineHeight="19.6px"
                        htmlFor="capacidadeAcolhimento"
                      >
                        Capacidade de Atendimento*
                      </FormLabel>
                      <Input
                        {...field}
                        h="42px"
                        name="capacidadeAcolhimento"
                        id="capacidadeAcolhimento"
                        backgroundColor="#F9FAFB"
                        autoComplete="off"
                        border="1px solid #D7D8DE"
                        placeholder="Informe Capacidade"
                        width={["100%", "100%", "30%"]}
                        maxLength={4}
                        onChange={(e) => {
                          form.setFieldValue(
                            "capacidadeAcolhimento",
                            numbersMask(e.target.value),
                          );
                        }}
                      />
                      <FormErrorMessage>
                        {form.errors.capacidadeAcolhimento}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              </Group>
            </Box>
            <Flex mt="30px" justifyContent="flex-end">
              <Button
                id="button-salvar"
                type="submit"
                translate="no"
                color="#FFF"
                backgroundColor="#169499"
                fontWeight={700}
                fontSize="15px"
                lineHeight="21.6px"
                _hover={{ bg: "#169499" }}
                w="17%"
                isLoading={props.isSubmitting}
              >
                Salvar
              </Button>
            </Flex>
          </Form>
        )}
      </Formik>
    ) : (
      <Box mt={4}>
        <SemRegistros buttonVisible={true} half />
      </Box>
    )
  ) : (
    <Flex w="100%" mt="25%" justifyContent="center">
      <LoadingSpinner />
    </Flex>
  );
};

export default CadastrarPerfil;
